import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BarChart, Bar, PieChart, Pie, AreaChart, Area, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell
} from 'recharts';
import {
  kpiData, eventTypeStatistics, severityDistribution, hourlyEventTrend, topThreats,
  nodeSecurityStatus, processControlStats, fileAccessControlStats, networkControlStats,
  complianceData, policyEffectiveness, weeklyTrend, clusterHealth, performanceMetrics,
  threatIntelligence, resourceSecurityStatus, policyRecommendations, policyStatistics
} from '../services/mockDashboardData';
import { useLanguage } from '../contexts/LanguageContext';
import { useColorTheme } from '../contexts/ColorThemeContext';
import VersionMismatchModal from './VersionMismatchModal';
import TooltipComponent from './Tooltip';
import './Dashboard.css';

// Agent Status 통계 카드 컴포넌트
const AgentStatusCard: React.FC<{
  label: string;
  value: number;
  status: 'running' | 'error' | 'stopped' | 'total';
}> = ({ label, value, status }) => {
  return (
    <div className={`agent-stat-card agent-stat-card-${status}`}>
      <div className="stat-label">{label}</div>
      <div className="stat-value">{value}</div>
    </div>
  );
};

// Agent Status 빠른 보기 컴포넌트
const AgentStatusQuickView: React.FC = () => {
  const { t } = useLanguage();

  const agentStats = [
    { label: t('agentStatus.running'), value: 4, status: 'running' as const },
    { label: t('agentStatus.error'), value: 1, status: 'error' as const },
    { label: t('agentStatus.stopped'), value: 0, status: 'stopped' as const },
    { label: t('agentStatus.total'), value: 5, status: 'total' as const },
  ];

  return (
    <div className="agent-status-quick">
      {agentStats.map((stat, idx) => (
        <AgentStatusCard
          key={idx}
          label={stat.label}
          value={stat.value}
          status={stat.status}
        />
      ))}
    </div>
  );
};

// 컬러 테마별 차트 색상 팔레트
const colorThemePalettes = {
  'sage-green': ['#5A7863', '#90AB8B', '#EBF4DD', '#3B4953', '#7a9d7f', '#a8c89f', '#4a6652', '#c4dab6'],
  'ocean-blue': ['#0066cc', '#3399ff', '#0099ff', '#004499', '#1a7fff', '#4da6ff', '#003d7a', '#66b3ff'],
  'sunset-orange': ['#ff6b35', '#ff8c5a', '#ffa366', '#d94a1f', '#ff7d47', '#ffb399', '#cc3d19', '#ffc4a0'],
  'purple-night': ['#7c3aed', '#a78bfa', '#c4b5fd', '#5b21b6', '#8b5cf6', '#d8b4fe', '#4c1d95', '#ddd6fe'],
};

const Dashboard: React.FC = () => {
  const { t } = useLanguage();
  const { colorTheme } = useColorTheme();
  const [showVersionMismatchModal, setShowVersionMismatchModal] = useState(false);

  // 버전 불일치 데이터
  const versionMismatches = [
    {
      nodeName: 'worker-node-4',
      nodeIP: '192.168.1.104',
      agentVersion: '0.10.1',
      kubeArmorVersion: '0.10.2',
      status: 'error' as const,
      recommendation: 'Update KubeArmor agent to version 0.10.2 to match the cluster version. Run: kubectl set image daemonset/kubearmor kubearmor=kubearmor:0.10.2 -n kube-system',
    },
  ];
  
  // 컬러 테마에 따라 데이터의 색상 변경
  const getColoredData = useMemo(() => {
    const palette = colorThemePalettes[colorTheme];
    return {
      eventTypeStatistics: eventTypeStatistics.map((item, idx) => ({
        ...item,
        color: palette[idx % palette.length],
      })),
      severityDistribution: severityDistribution.map((item, idx) => ({
        ...item,
        color: palette[idx % palette.length],
      })),
      policyStatistics: {
        byType: policyStatistics.byType.map((item, idx) => ({
          ...item,
          color: palette[idx % palette.length],
        })),
        byStatus: policyStatistics.byStatus.map((item, idx) => ({
          ...item,
          color: palette[idx % palette.length],
        })),
      },
      fileAccessControlStats: {
        ...fileAccessControlStats,
        byType: fileAccessControlStats.byType.map((item, idx) => ({
          ...item,
          color: palette[idx % palette.length],
        })),
      },
      complianceData: complianceData.byFramework.map((item, idx) => ({
        ...item,
        color: palette[idx % palette.length],
      })),
    };
  }, [colorTheme]);

  const navigate = useNavigate();

  return (
    <div className="dashboard-comprehensive">
      {/* ============================================================================ */}
      {/* SECTION 1: KPI Cards - 핵심 지표 */}
      {/* ============================================================================ */}
      <section className="dashboard-section">
        <TooltipComponent title={t('coreMetrics.title')} description={t('coreMetrics.description') || ''}>
          <h2 className="section-title">{t('coreMetrics.title')}</h2>
        </TooltipComponent>
        <div className="kpi-grid">
          <div className="kpi-card kpi-primary" onClick={() => navigate('/analysis/events')} style={{cursor: 'pointer'}}>
            <div className="kpi-icon">📈</div>
            <div className="kpi-label">{t('coreMetrics.totalEvents')}</div>
            <div className="kpi-value">{kpiData.totalEvents}</div>
            <div className="kpi-meta">+{kpiData.eventsToday} {t('coreMetrics.today')}</div>
          </div>
          <div className="kpi-card kpi-success">
            <div className="kpi-icon">✓</div>
            <div className="kpi-label">{t('coreMetrics.allowedEvents')}</div>
            <div className="kpi-value">{kpiData.allowedEvents}</div>
            <div className="kpi-meta">{((kpiData.allowedEvents/kpiData.totalEvents)*100).toFixed(1)}%</div>
          </div>
          <div className="kpi-card kpi-danger">
            <div className="kpi-icon">🛑</div>
            <div className="kpi-label">{t('coreMetrics.blockedEvents')}</div>
            <div className="kpi-value">{kpiData.blockedEvents}</div>
            <div className="kpi-meta">{((kpiData.blockedEvents/kpiData.totalEvents)*100).toFixed(1)}%</div>
          </div>
          <div className="kpi-card kpi-info">
            <div className="kpi-icon">📋</div>
            <div className="kpi-label">{t('coreMetrics.activePolicies')}</div>
            <div className="kpi-value">{kpiData.activePolicies}</div>
            <div className="kpi-meta">{t('coreMetrics.of')} {kpiData.totalPolicies} {t('coreMetrics.total')}</div>
          </div>
          <div className="kpi-card kpi-warning">
            <div className="kpi-icon">⚠️</div>
            <div className="kpi-label">{t('coreMetrics.criticalAlerts')}</div>
            <div className="kpi-value">{kpiData.criticalAlerts}</div>
            <div className="kpi-meta">{t('coreMetrics.immediateAttention')}</div>
          </div>
          <div className="kpi-card kpi-score">
            <div className="kpi-icon">🎯</div>
            <div className="kpi-label">{t('coreMetrics.securityScore')}</div>
            <div className="kpi-value">{kpiData.securityScore}</div>
            <div className="kpi-meta">/ 100</div>
          </div>
          <div className="kpi-card kpi-success">
            <div className="kpi-icon">✓</div>
            <div className="kpi-label">{t('coreMetrics.complianceRate')}</div>
            <div className="kpi-value">{kpiData.complianceRate}%</div>
            <div className="kpi-meta">{t('coreMetrics.regulatory')}</div>
          </div>
          <div className="kpi-card kpi-info">
            <div className="kpi-icon">🖥️</div>
            <div className="kpi-label">{t('coreMetrics.protectedNodes')}</div>
            <div className="kpi-value">{kpiData.protectedNodes}</div>
            <div className="kpi-meta">{t('coreMetrics.of')} {kpiData.totalNodes} nodes</div>
          </div>
        </div>
      </section>

      {/* ============================================================================ */}
      {/* SECTION 2: 이벤트 분석 */}
      {/* ============================================================================ */}
      <section className="dashboard-section">
        <TooltipComponent title={t('eventAnalysis.title')} description={t('eventAnalysis.description') || ''}>
          <h2 className="section-title" onClick={() => navigate('/analysis/events')} style={{cursor: 'pointer'}}>
            {t('eventAnalysis.title')}
          </h2>
        </TooltipComponent>
        <div className="grid-2">
          {/* 이벤트 타입별 분포 */}
          <div className="chart-card compact" onClick={() => navigate('/analysis/events')} style={{cursor: 'pointer'}}>
            <h3>{t('eventAnalysis.eventsByType')}</h3>
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie
                  data={getColoredData.eventTypeStatistics}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percentage }) => `${name}: ${percentage.toFixed(1)}%`}
                  outerRadius={70}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {getColoredData.eventTypeStatistics.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>

          {/* 심각도별 분포 */}
          <div className="chart-card compact" onClick={() => navigate('/analysis/events')} style={{cursor: 'pointer'}}>
            <h3>{t('eventAnalysis.severityDistribution')}</h3>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={getColoredData.severityDistribution}>
                <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                <XAxis dataKey="severity" tick={{ fill: '#aaa', fontSize: 12 }} />
                <YAxis tick={{ fill: '#aaa', fontSize: 12 }} />
                <Tooltip />
                <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                  {getColoredData.severityDistribution.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* 시간별 트렌드 */}
        <div className="chart-card full-width" style={{ marginTop: '1rem' }}>
          <h3>{t('eventAnalysis.timeline24h')}</h3>
          <ResponsiveContainer width="100%" height={200}>
            <AreaChart data={hourlyEventTrend}>
              <defs>
                <linearGradient id="colorTotal" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={colorThemePalettes[colorTheme][0]} stopOpacity={0.8} />
                  <stop offset="95%" stopColor={colorThemePalettes[colorTheme][0]} stopOpacity={0.1} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#333" />
              <XAxis dataKey="time" tick={{ fill: '#aaa', fontSize: 11 }} />
              <YAxis tick={{ fill: '#aaa', fontSize: 11 }} />
              <Tooltip />
              <Area type="monotone" dataKey="total" stroke={colorThemePalettes[colorTheme][0]} fill="url(#colorTotal)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </section>

      {/* ============================================================================ */}
      {/* SECTION 3: 정책 분석 */}
      {/* ============================================================================ */}
      <section className="dashboard-section">
        <TooltipComponent title={t('policyAnalysis.title')} description={t('policyAnalysis.description') || ''}>
          <h2 className="section-title">{t('policyAnalysis.title')}</h2>
        </TooltipComponent>
        <div className="grid-3">
          {/* 정책 타입별 */}
          <div className="chart-card compact" onClick={() => navigate('/analysis/policies')}>
            <h3>{t('policyAnalysis.policiesByType')}</h3>
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie
                  data={getColoredData.policyStatistics.byType}
                  cx="50%"
                  cy="50%"
                  innerRadius={40}
                  outerRadius={70}
                  fill="#8884d8"
                  dataKey="value"
                  label={({ name, value }) => `${name}\n${value}`}
                >
                  {getColoredData.policyStatistics.byType.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>

          {/* 정책 상태 */}
          <div className="chart-card compact" onClick={() => navigate('/analysis/policies')}>
            <h3>{t('policyAnalysis.policyStatus')}</h3>
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie
                  data={getColoredData.policyStatistics.byStatus}
                  cx="50%"
                  cy="50%"
                  innerRadius={40}
                  outerRadius={70}
                  fill="#8884d8"
                  dataKey="value"
                  label={({ name, value }) => `${name}\n${value}`}
                >
                  {getColoredData.policyStatistics.byStatus.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>

          {/* 정책 효과성 Top 5 */}
          <div className="table-card compact policy-effectiveness-card" onClick={() => navigate('/analysis/policies')}>
            <h3>{t('policyAnalysis.topPoliciesEffectiveness')}</h3>
            <div className="mini-table">
              {policyEffectiveness.slice(0, 5).map((policy, idx) => (
                <div key={idx} className="mini-table-row">
                  <div className="table-cell-small">{idx + 1}.</div>
                  <div className="table-cell-name">{policy.policyName}</div>
                  <div className="table-cell-stat">{policy.effectiveness.toFixed(1)}%</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ============================================================================ */}
      {/* SECTION 4: 보안 제어 (Process, File, Network) */}
      {/* ============================================================================ */}
      <section className="dashboard-section">
        <TooltipComponent title={t('securityControls.title')} description={t('securityControls.description') || ''}>
          <h2 className="section-title">{t('securityControls.title')}</h2>
        </TooltipComponent>
        <div className="security-controls-grid">
          {/* 프로세스 제어 */}
          <div className="security-control-card" onClick={() => navigate('/analysis/security-controls')}>
            <div className="card-header">
              <h3>{t('securityControls.processExecution')}</h3>
            </div>
            <div className="card-stats">
              <div className="stat-row">
                <span>{t('securityControls.allowed')}:</span>
                <span className="stat-value success">{processControlStats.whitelist.allowed}</span>
              </div>
              <div className="stat-row">
                <span>{t('securityControls.blocked')}:</span>
                <span className="stat-value danger">{processControlStats.whitelist.blocked}</span>
              </div>
            </div>
            <div className="chart-container">
              <BarChart width={280} height={150} data={processControlStats.byType}>
                <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                <XAxis dataKey="type" tick={{ fill: '#aaa', fontSize: 10 }} angle={-15} />
                <YAxis tick={{ fill: '#aaa', fontSize: 11 }} />
                <Bar dataKey="count" fill={colorThemePalettes[colorTheme][0]} />
              </BarChart>
            </div>
          </div>

          {/* 파일 접근 제어 */}
          <div className="security-control-card" onClick={() => navigate('/analysis/security-controls')}>
            <div className="card-header">
              <h3>{t('securityControls.fileAccess')}</h3>
            </div>
            <div className="chart-container">
              <BarChart width={280} height={150} data={getColoredData.fileAccessControlStats.byType}>
                <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                <XAxis dataKey="type" tick={{ fill: '#aaa', fontSize: 11 }} />
                <YAxis tick={{ fill: '#aaa', fontSize: 11 }} />
                <Tooltip />
                <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                  {getColoredData.fileAccessControlStats.byType.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Bar>
              </BarChart>
            </div>
          </div>

          {/* 네트워크 제어 */}
          <div className="security-control-card" onClick={() => navigate('/analysis/security-controls')}>
            <div className="card-header">
              <h3>{t('securityControls.networkControl')}</h3>
            </div>
            <div className="card-stats">
              <div className="network-stat-box">
                <div className="network-stat-label">{t('securityControls.inbound')}</div>
                <div className="network-stat-row">
                  <span>✓ {networkControlStats.inbound.allowed}</span>
                  <span>✗ {networkControlStats.inbound.blocked}</span>
                </div>
              </div>
              <div className="network-stat-box">
                <div className="network-stat-label">{t('securityControls.outbound')}</div>
                <div className="network-stat-row">
                  <span>✓ {networkControlStats.outbound.allowed}</span>
                  <span>✗ {networkControlStats.outbound.blocked}</span>
                </div>
              </div>
            </div>
            <div className="chart-container">
              <BarChart width={280} height={150} data={networkControlStats.byProtocol}>
                <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                <XAxis dataKey="protocol" tick={{ fill: '#aaa', fontSize: 11 }} />
                <YAxis tick={{ fill: '#aaa', fontSize: 11 }} />
                <Bar dataKey="allowed" fill={colorThemePalettes[colorTheme][1]} stackId="a" />
                <Bar dataKey="blocked" fill={colorThemePalettes[colorTheme][0]} stackId="a" />
              </BarChart>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================================================ */}
      {/* SECTIONS 5-6: 정책 위반 & 노드 보안 (Side by Side) */}
      {/* ============================================================================ */}
      <div className="grid-2">
        {/* SECTION 5: 정책 위반 및 공격 패턴 */}
        <section className="dashboard-section">
          <TooltipComponent title={t('policyViolations.title')} description={t('policyViolations.description') || ''}>
            <h2 className="section-title" onClick={() => navigate('/analysis/policy-violations')} style={{cursor: 'pointer'}}>
              {t('policyViolations.title')}
            </h2>
          </TooltipComponent>
          <div className="threats-table">
            {topThreats.map((threat, idx) => (
              <div key={idx} className={`threat-row threat-${threat.severity.toLowerCase()}`} onClick={() => navigate('/analysis/policy-violations')} style={{cursor: 'pointer'}}>
                <div className="threat-rank">#{threat.rank}</div>
                <div className="threat-info">
                  <div className="threat-name">{threat.threat}</div>
                  <div className="threat-details">
                    <span className="threat-ns">{threat.namespace}/{threat.pod}</span>
                    <span className="threat-policy">{t('policyViolations.title')}: {threat.policy}</span>
                  </div>
                </div>
                <div className="threat-stats">
                  <span className="threat-count">{threat.count} {t('threats.events')}</span>
                  <span className={`threat-severity threat-sev-${threat.severity.toLowerCase()}`}>
                    {threat.severity}
                  </span>
                  <span className="threat-action">{threat.action}</span>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* SECTION 6: 노드 & 호스트 보안 */}
        <section className="dashboard-section">
          <TooltipComponent title={t('nodeHost.title')} description={t('nodeHost.description') || ''}>
            <h2 className="section-title" onClick={() => navigate('/analysis/nodes')} style={{cursor: 'pointer'}}>
              {t('nodeHost.title')}
            </h2>
          </TooltipComponent>
          <div className="nodes-table">
            <div className="nodes-table-header">
              <div className="col-1">{t('nodeHost.nodeName')}</div>
              <div className="col-2">{t('nodeHost.status')}</div>
              <div className="col-3">{t('nodeHost.protection')}</div>
              <div className="col-4">{t('nodeHost.policies')}</div>
              <div className="col-5">{t('nodeHost.events24h')}</div>
              <div className="col-6">{t('nodeHost.blocked')}</div>
            </div>
            {nodeSecurityStatus.map((node, idx) => (
              <div key={idx} className="nodes-table-row">
                <div className="col-1">{node.nodeName}</div>
                <div className="col-2"><span className="badge-success">✓ {t('nodeHost.healthy')}</span></div>
                <div className="col-3"><span className="badge-info">{node.protectionLevel}</span></div>
                <div className="col-4">{node.policies}</div>
                <div className="col-5">{node.events24h}</div>
                <div className="col-6"><span className="text-danger">{node.blocked}</span></div>
              </div>
            ))}
          </div>
        </section>
      </div>

      {/* ============================================================================ */}
      {/* SECTION 7: 컴플라이언스 & 감사 */}
      {/* ============================================================================ */}
      <section className="dashboard-section">
        <TooltipComponent title={t('compliance.title')} description={t('compliance.description') || ''}>
          <h2 className="section-title" onClick={() => navigate('/analysis/compliance')} style={{cursor: 'pointer'}}>
            {t('compliance.title')}
          </h2>
        </TooltipComponent>
        <div className="grid-2">
          {/* 컴플라이언스 점수 */}
          <div className="chart-card compact" onClick={() => navigate('/analysis/compliance')} style={{cursor: 'pointer'}}>
            <h3>{t('compliance.complianceScore')}</h3>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={getColoredData.complianceData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                <XAxis dataKey="framework" tick={{ fill: '#aaa', fontSize: 11 }} angle={-15} />
                <YAxis tick={{ fill: '#aaa', fontSize: 11 }} domain={[0, 100]} />
                <Tooltip />
                <Bar dataKey="compliance" radius={[4, 4, 0, 0]}>
                  {getColoredData.complianceData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* 감사 로그 */}
          <div className="audit-table compact">
            <h3>{t('compliance.recentAuditEvents')}</h3>
            {complianceData.policyAudit.map((audit, idx) => (
              <div key={idx} className="audit-row">
                <div className="audit-time">{audit.timestamp}</div>
                <div className="audit-event">{audit.event}</div>
                <div className="audit-user">{audit.user}</div>
                <div className="audit-status"><span className="badge-success">✓</span></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============================================================================ */}
      {/* SECTION 8: 클러스터 건강도 & 성능 */}
      {/* ============================================================================ */}
      <section className="dashboard-section">
        <TooltipComponent title={t('clusterHealth.title')} description={t('clusterHealth.description') || ''}>
          <h2 className="section-title">{t('clusterHealth.title')}</h2>
        </TooltipComponent>
        <div className="grid-2">
          {/* 클러스터 건강도 */}
          <div className="chart-card compact">
            <h3>{t('clusterHealth.healthStatus')}</h3>
            <div className="health-score">
              <div className="health-score-value">{clusterHealth.score}</div>
              <div className="health-score-label">/ 100</div>
            </div>
            <div className="health-components">
              {clusterHealth.components.map((component, idx) => (
                <div key={idx} className="health-component">
                  <div className="component-name">{component.component}</div>
                  <div className="component-bar">
                    <div
                      className="component-progress"
                      style={{ width: `${component.percentage}%` }}
                    ></div>
                  </div>
                  <div className="component-percent">{component.percentage}%</div>
                </div>
              ))}
            </div>
          </div>

          {/* 성능 메트릭 */}
          <div className="performance-card compact">
            <h3>{t('clusterHealth.performanceMetrics')}</h3>
            <div className="perf-metric">
              <div className="perf-title">{t('clusterHealth.eventProcessingLatency')}</div>
              <div className="perf-values">
                <div><span>P50:</span> <span className="perf-value">{performanceMetrics.eventProcessingLatency.p50}ms</span></div>
                <div><span>P95:</span> <span className="perf-value">{performanceMetrics.eventProcessingLatency.p95}ms</span></div>
                <div><span>P99:</span> <span className="perf-value">{performanceMetrics.eventProcessingLatency.p99}ms</span></div>
              </div>
            </div>
            <div className="perf-metric">
              <div className="perf-title">{t('clusterHealth.resourceUsage')}</div>
              <div className="perf-values">
                <div><span>{t('clusterHealth.cpu')}:</span> <span className="perf-value">{performanceMetrics.systemResourceUsage.cpuUsage}%</span></div>
                <div><span>{t('clusterHealth.memory')}:</span> <span className="perf-value">{performanceMetrics.systemResourceUsage.memoryUsage}%</span></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================================================ */}
      {/* SECTION 9: 주간 추세 */}
      {/* ============================================================================ */}
      <section className="dashboard-section">
        <TooltipComponent title={t('weeklyTrends.title')} description={t('weeklyTrends.description') || ''}>
          <h2 className="section-title">{t('weeklyTrends.title')}</h2>
        </TooltipComponent>
        <div className="chart-card full-width">
          <ResponsiveContainer width="100%" height={250}>
            <LineChart data={weeklyTrend}>
              <CartesianGrid strokeDasharray="3 3" stroke="#333" />
              <XAxis dataKey="date" tick={{ fill: '#aaa' }} />
              <YAxis tick={{ fill: '#aaa' }} />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="events" stroke={colorThemePalettes[colorTheme][0]} strokeWidth={2} />
              <Line type="monotone" dataKey="blocked" stroke={colorThemePalettes[colorTheme][3]} strokeWidth={2} />
              <Line type="monotone" dataKey="threats" stroke={colorThemePalettes[colorTheme][1]} strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </section>

      {/* ============================================================================ */}
      {/* SECTION 10: 위협 인텔리전스 */}
      {/* ============================================================================ */}
      <section className="dashboard-section">
        <TooltipComponent title={t('threatIntel.title')} description={t('threatIntel.description') || ''}>
          <h2 className="section-title">{t('threatIntel.title')}</h2>
        </TooltipComponent>
        <div className="threat-intel">
          <div className="mitre-coverage">
            <h3>{t('threatIntel.mitreCoverage')}</h3>
            <div className="coverage-score">{threatIntelligence.mitreCoverage}%</div>
          </div>
          <div className="recent-detections">
            <h3>{t('threatIntel.recentDetections')}</h3>
            {threatIntelligence.recentDetections.map((detection, idx) => (
              <div key={idx} className={`detection-item detection-${detection.severity.toLowerCase()}`}>
                <div className="detection-header">
                  <span className="detection-id">{detection.id}</span>
                  <span className="detection-name">{detection.name}</span>
                  <span className={`detection-severity detection-sev-${detection.severity.toLowerCase()}`}>
                    {detection.severity}
                  </span>
                  <span className={`detection-action ${detection.blocked ? 'blocked' : ''}`}>
                    {detection.blocked ? `🛑 ${t('threatIntel.blocked')}` : `⚠️ ${t('threatIntel.detected')}`}
                  </span>
                </div>
                <div className="detection-details">
                  <span>{detection.mitreTechnique}</span>
                  {detection.cveIds.length > 0 && <span>CVE: {detection.cveIds.join(', ')}</span>}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============================================================================ */}
      {/* SECTION 11: 리소스별 보안 */}
      {/* ============================================================================ */}
      <section className="dashboard-section">
        <TooltipComponent title={t('resourceSecurity.title')} description={t('resourceSecurity.description') || ''}>
          <h2 className="section-title">{t('resourceSecurity.title')}</h2>
        </TooltipComponent>
        <div className="resources-table">
          <div className="resources-table-header">
            <div className="col-1">{t('resourceSecurity.resource')}</div>
            <div className="col-2">{t('resourceSecurity.type')}</div>
            <div className="col-3">{t('resourceSecurity.namespace')}</div>
            <div className="col-4">{t('resourceSecurity.protectedPods')}</div>
            <div className="col-5">{t('common.status')}</div>
            <div className="col-6">{t('threats.events')}</div>
            <div className="col-7">{t('resourceSecurity.threatLevel')}</div>
          </div>
          {resourceSecurityStatus.map((resource, idx) => (
            <div key={idx} className="resources-table-row">
              <div className="col-1">{resource.resource}</div>
              <div className="col-2"><span className="badge-secondary">{resource.type}</span></div>
              <div className="col-3">{resource.namespace}</div>
              <div className="col-4">{resource.protectedPods}/{resource.totalPods}</div>
              <div className="col-5">{resource.policiesApplied}</div>
              <div className="col-6">{resource.recentEvents}</div>
              <div className="col-7">
                <span className={`threat-level threat-${resource.threatLevel.toLowerCase()}`}>
                  {resource.threatLevel}
                </span>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ============================================================================ */}
      {/* SECTION 12: 정책 권장사항 */}
      {/* ============================================================================ */}
      <section className="dashboard-section">
        <TooltipComponent title={t('recommendations.title')} description={t('recommendations.description') || ''}>
          <h2 className="section-title">{t('recommendations.title')}</h2>
        </TooltipComponent>
        <div className="recommendations">
          {policyRecommendations.map((rec, idx) => (
            <div key={idx} className={`recommendation-card rec-${rec.severity.toLowerCase()}`}>
              <div className="rec-header">
                <div className="rec-title">{rec.title}</div>
                <div className="rec-severity">{rec.severity}</div>
              </div>
              <div className="rec-description">{rec.description}</div>
              <div className="rec-footer">
                <span className="rec-framework">{rec.framework}</span>
                <span className="rec-impact">{rec.estimatedImpact}</span>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ============================================================================ */}
      {/* SECTION 9: KubeArmor Agent Status */}
      {/* ============================================================================ */}
      <section className="dashboard-section">
        <TooltipComponent title={t('agentStatus.title')} description={t('agentStatus.description') || ''}>
          <h2 className="section-title" onClick={() => navigate('/analysis/agent-status')} style={{cursor: 'pointer'}}>
            {t('agentStatus.title')} 
          </h2>
        </TooltipComponent>
        <AgentStatusQuickView />
        <div 
          className="agent-warning-banner"
          onClick={() => setShowVersionMismatchModal(true)}
          role="button"
          tabIndex={0}
        >
          <span>⚠️ {t('agentStatus.versionMismatchWarning')}</span>
        </div>
      </section>

      {/* Version Mismatch Modal */}
      <VersionMismatchModal
        isOpen={showVersionMismatchModal}
        onClose={() => setShowVersionMismatchModal(false)}
        mismatches={versionMismatches}
      />
    </div>
  );
};

export default Dashboard;
